import { useEffect, useState } from "react";
import { useBrowserSettings } from "../../../../hooks/useBrowserSettings";
import {
  budgetDraft,
  type BrowserSettings,
  type BrowserSettingsView,
  DAILY_MINUTES_MAX,
  MONTHLY_MINUTES_MAX,
  parseBudgetMinutes,
} from "../../../../lib/browserSettings";
import { Skeleton } from "../../../dashboard/Skeleton";
import { type Outcome, SwitchRow, useMutation } from "./AdminSettingsPanel";
import styles from "./AdminSettingsPanel.module.css";

type BudgetsRowProps = {
  readonly settings: BrowserSettingsView;
  readonly onSave: (dailyMinutes: number | null, monthlyMinutes: number | null) => Promise<Outcome>;
};

const BudgetsRow = ({ settings, onSave }: BudgetsRowProps): JSX.Element => {
  const [daily, setDaily] = useState(budgetDraft(settings.dailyMinutes));
  const [monthly, setMonthly] = useState(budgetDraft(settings.monthlyMinutes));
  const { busy, error, run } = useMutation();
  useEffect(() => setDaily(budgetDraft(settings.dailyMinutes)), [settings.dailyMinutes]);
  useEffect(() => setMonthly(budgetDraft(settings.monthlyMinutes)), [settings.monthlyMinutes]);
  const dailyMinutes = parseBudgetMinutes(daily, DAILY_MINUTES_MAX);
  const monthlyMinutes = parseBudgetMinutes(monthly, MONTHLY_MINUTES_MAX);
  const changed = dailyMinutes !== settings.dailyMinutes || monthlyMinutes !== settings.monthlyMinutes;
  const ready = dailyMinutes !== "invalid" && monthlyMinutes !== "invalid" && changed;
  return (
    <li className={styles.row}>
      <div className={styles.fieldRow}>
        <span className={styles.text}>
          <span className={styles.label}>Browser minutes</span>
          <span className={styles.hint}>Per UTC day and month. Leave a box empty for the default.</span>
        </span>
        <span className={styles.inputGroup}>
          <input aria-label="Daily browser minutes" placeholder="Daily" inputMode="numeric" className={styles.input}
            value={daily} onChange={(e) => setDaily(e.target.value)} disabled={busy} />
          <input aria-label="Monthly browser minutes" placeholder="Monthly" inputMode="numeric" className={styles.input}
            value={monthly} onChange={(e) => setMonthly(e.target.value)} disabled={busy} />
          <button type="button" className={styles.save} disabled={busy || !ready}
            onClick={() => {
              if (dailyMinutes !== "invalid" && monthlyMinutes !== "invalid") void run(() => onSave(dailyMinutes, monthlyMinutes));
            }}>
            {busy ? "Saving..." : "Save"}
          </button>
        </span>
      </div>
      {error !== null && <span className={styles.error} role="status">{error}</span>}
    </li>
  );
};

type BrowserSettingsCardProps = {
  readonly status: "loading" | "ready" | "error";
  readonly settings: BrowserSettingsView | null;
  readonly error: string | null;
  readonly save: (next: BrowserSettings) => Promise<Outcome>;
};

const budgetsOf = ({ dailyMinutes, monthlyMinutes }: BrowserSettings): Pick<BrowserSettings, "dailyMinutes" | "monthlyMinutes"> =>
  ({ dailyMinutes, monthlyMinutes });

export const BrowserSettingsCard = ({ status, settings, error, save }: BrowserSettingsCardProps): JSX.Element => {
  const recording = useMutation();
  return (
    <section className={styles.card} aria-label="Browser">
      <h3 className={styles.title}>Browser</h3>
      {settings === null ? (
        status === "error" ? <p className={styles.error} role="status">{error}</p> : (
          <ul className={styles.list} aria-busy>
            <li className={styles.row}><Skeleton width="60%" height="2rem" /></li>
            <li className={styles.row}><Skeleton width="80%" height="2rem" /></li>
          </ul>
        )
      ) : (
        <ul className={styles.list}>
          <SwitchRow label="Record browser sessions"
            hint="Saves what the browser shows as videos the agent can find and share. Kept 14 days."
            on={settings.recordingEnabled} disabled={recording.busy} error={recording.error}
            onToggle={() => void recording.run(() => save({ ...budgetsOf(settings), recordingEnabled: !settings.recordingEnabled }))} />
          <BudgetsRow settings={settings}
            onSave={(dailyMinutes, monthlyMinutes) => save({ recordingEnabled: settings.recordingEnabled, dailyMinutes, monthlyMinutes })} />
        </ul>
      )}
    </section>
  );
};

export const BrowserSettingsPanel = ({ tenantId }: { readonly tenantId: string }): JSX.Element => {
  const { status, settings, error, save } = useBrowserSettings(tenantId);
  return <BrowserSettingsCard status={status} settings={settings} error={error} save={save} />;
};
